import React, { useState, useEffect } from "react";
import { Outlet, useLocation, Navigate } from "react-router-dom";
import Sidebar from "@/components/Sidebar";
import ImpersonationBanner, { isImpersonating } from "@/components/ImpersonationBanner";
import { Toaster } from "@/components/ui/sonner";
import { Menu } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import CommercialistaModeBar from "@/components/CommercialistaModeBar";
import CommercialistaBreadcrumbs from "@/components/CommercialistaBreadcrumbs";

export default function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();

  // Close mobile sidebar on route change
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  // Admin users have a dedicated console at /app/admin: they must never
  // land on the regular user dashboard. If we detect an admin under /app/*
  // (but NOT under /app/admin/*, which is a top-level route in App.js),
  // redirect them to the admin console — EXCEPT when they are actively
  // impersonating a regular user, in which case they need to see /app.
  if (user?.is_admin && !isImpersonating()) {
    return <Navigate to="/app/admin" replace />;
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="app-layout">
      <ImpersonationBanner />
      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-[#FAF9F6]/95 backdrop-blur-md border-b border-[#E8E4D9] h-14 flex items-center px-4 justify-between">
        <button
          onClick={() => setSidebarOpen(true)}
          data-testid="mobile-menu-btn"
          className="p-2 rounded-md hover:bg-white transition-colors"
          aria-label="Apri menu"
        >
          <Menu className="w-5 h-5" />
        </button>
        <div className="font-display font-extrabold text-base flex items-center gap-1.5">
          Autofattura by
          <img src="/logo.png" alt="Dasabo" className="h-4 w-auto" />
        </div>
        <div className="w-9" />
      </div>

      {/* Overlay for mobile sidebar */}
      {sidebarOpen && (
        <button
          type="button"
          aria-label="Chiudi menu"
          className="lg:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm animate-in fade-in"
          onClick={() => setSidebarOpen(false)}
          data-testid="mobile-overlay"
        />
      )}

      <div className="flex">
        <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <main className="flex-1 min-w-0 pt-14 lg:pt-0">
          {user?.is_commercialista && <CommercialistaModeBar />}
          {user?.is_commercialista && <CommercialistaBreadcrumbs />}
          <Outlet />
        </main>
      </div>

      <Toaster richColors position="top-right" />
    </div>
  );
}
